import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import type { IB2B_list } from "src/content/modules/B2B/type/b2bType";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import type { IAccountData } from "src/Types/account.types";
import type { IPayableItem } from "src/Types/module/paymentableItem/itemList";

export interface IInvoiceItemRow {
	id: number | "";
	quantity: number;
	amount: number;
}

interface Props {
	handleClose?: () => void;
	onSuccess?: () => void;
}

const CreateB2BInvoiceForm: React.FC<Props> = ({ handleClose, onSuccess }) => {
	// Basic fields
	const [selectedB2B, setSelectedB2B] = useState<IB2B_list | null>(null);
	const [invoiceDate, setInvoiceDate] = useState<string>(
		dayjs().format("YYYY-MM-DD")
	);
	const [dueDate, setDueDate] = useState<string>(
		dayjs().add(7, "day").format("YYYY-MM-DD")
	);
	const [remark, setRemark] = useState<string>("");

	// Calculation fields
	const [discount, setDiscount] = useState<number | "">(0);
	const [vat, setVat] = useState<number | "">(0);

	// Money receipt toggle & fields
	const [withMoneyReceipt, setWithMoneyReceipt] = useState<boolean>(false);
	const [paymentMethod, setPaymentMethod] = useState<string>("Cash");
	const [accountId, setAccountId] = useState<number | "">("");
	const [chequeNo, setChequeNo] = useState<string>("");

	// Items state
	const [items, setItems] = useState<IInvoiceItemRow[]>([
		{
			id: "",
			quantity: 1,
			amount: 0,
		},
	]);

	// Fetch B2B members
	const [b2bSearch, setB2bSearch] = useState<string>("");
	const { data: b2bData, isLoading: isB2BLoading } = useGetQueryWithTotal<
		IB2B_list[]
	>({
		endPoint: "/api/fair/admin/b2b",
		queryKeys: ["B2B-invoice-create", b2bSearch],
		params: { name: b2bSearch },
	});

	// Fetch Payable items
	const { data: payableItems = [], isLoading: isItemsLoading } = useApiQuery<
		IPayableItem[]
	>({
		endPoint: "/api/admin/payment-items",
		queryKeys: ["PayableItems"],
	});

	// Fetch Accounts
	const { data: accounts = [] } = useApiQuery<IAccountData[]>({
		endPoint: "/api/fair/admin/account",
		queryKeys: ["Accounts", paymentMethod],
		params: paymentMethod ? { ac_type: paymentMethod } : {},
	});

	// Mutation
	const { mutate: createInvoice, isPending, isSuccess } = useApiMutation<any, any>({
		method: "POST",
		endPoint: "/api/admin/b2b/invoice",
		invalidateQueries: ["b2b_invoice"],
	});

	useEffect(() => {
		if (isSuccess) {
			setSelectedB2B(null);
			setB2bSearch("");
			setInvoiceDate(dayjs().format("YYYY-MM-DD"));
			setDueDate(dayjs().add(7, "day").format("YYYY-MM-DD"));
			setRemark("");
			setDiscount(0);
			setVat(0);
			setWithMoneyReceipt(false);
			setPaymentMethod("Cash");
			setAccountId("");
			setChequeNo("");
			setItems([
				{
					id: "",

					quantity: 1,
					amount: 0,
				},
			]);
			if (handleClose) handleClose();
			if (onSuccess) onSuccess();
		}
	}, [isSuccess]);

	// Sub Total calculation
	const subTotal = useMemo(() => {
		return items.reduce((sum, item) => {
			const qty = Number(item.quantity) || 0;
			const amt = Number(item.amount) || 0;
			return sum + qty * amt;
		}, 0);
	}, [items]);

	// Net Total calculation
	const netTotal = useMemo(() => {
		const disc = Number(discount) || 0;
		const v = Number(vat) || 0;
		return Math.max(0, subTotal - disc + v);
	}, [subTotal, discount, vat]);

	// Item change handlers
	const handleAddItem = () => {
		setItems((prev) => [
			...prev,
			{
				id: "",
				product_name: "",
				description: "",
				quantity: 1,
				amount: 0,
			},
		]);
	};

	const handleRemoveItem = (index: number) => {
		if (items.length <= 1) return;
		setItems((prev) => prev.filter((_, i) => i !== index));
	};

	const handleItemChange = (
		index: number,
		field: keyof IInvoiceItemRow,
		val: any
	) => {
		setItems((prev) => {
			const updated = [...prev];
			updated[index] = { ...updated[index], [field]: val };
			return updated;
		});
	};

	const handleSelectPayableItem = (index: number, payableItemId: number | "") => {
		const found = payableItems.find((p) => p.id === payableItemId);
		setItems((prev) => {
			const updated = [...prev];
			if (found) {
				updated[index] = {
					...updated[index],
					id: found.id,

					amount: Number(found.amount) || 0,
				};
			} else {
				updated[index] = {
					...updated[index],
					id: "",

					amount: 0,
				};
			}
			return updated;
		});
	};

	// Form submit handler
	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!selectedB2B?.b2b_id) {
			alert("Please select a B2B member.");
			return;
		}



		if (items.some((item) => !item.id)) {
			alert("Please select an item for each row.");
			return;
		}

		const payload = {
			b2b_id: selectedB2B.b2b_id,
			invoice_date: invoiceDate,
			due_date: dueDate,
			items: items.map((item) => {
				const found = payableItems.find((p) => p.id === item.id);
				return {
					id: item.id ? Number(item.id) : 0,
					product_name: found?.name || "Invoice Item",
					description: found?.name || "",
					quantity: Number(item.quantity) || 1,
					amount: Number(item.amount) || 0,
				};
			}),
			with_money_receipt: Boolean(withMoneyReceipt),
			...(withMoneyReceipt
				? {
					account_id: accountId ? Number(accountId) : undefined,
					payment_method: paymentMethod || undefined,
					...(paymentMethod === "Cheque" && chequeNo.trim()
						? { cheque_no: chequeNo.trim() }
						: {}),
				}
				: {}),
			remark: remark.trim(),
			sub_total: subTotal,
			vat: Number(vat) || 0,
			discount: Number(discount) || 0,
			net_total: netTotal,
		};

		createInvoice(payload);
	};

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.5,
				width: "100%",
				py: 0.5,
			}}
		>
			{/* Card 1: Invoice Details */}
			<Card
				variant="outlined"
				sx={{
					borderRadius: 2,
					borderColor: "divider",
					boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
				}}
			>
				<CardContent sx={{ px: 2, py: 1.25, "&:last-child": { pb: 1.25 } }}>
					<Typography
						variant="subtitle2"
						fontWeight={600}
						color="text.primary"
						mb={1}
					>
						Invoice Information
					</Typography>

					<Grid container spacing={2}>
						{/* B2B Member */}
						<Grid item xs={12} md={6}>
							<Autocomplete<IB2B_list, false, false, false>
								options={b2bData?.data || []}
								value={selectedB2B}
								loading={isB2BLoading}
								getOptionLabel={(option) => {
									if (!option) return "";
									const org = option.b2b_organization
										? `${option.b2b_organization} - `
										: "";
									const name = `${option.b2b_first_name || ""} ${option.b2b_last_name || ""
										}`.trim();
									return `${org}${name || "B2B Member"} (#${option.b2b_id})`;
								}}
								isOptionEqualToValue={(opt, val) => opt.b2b_id === val.b2b_id}
								onInputChange={(_, val, reason) => {
									if (reason === "input") {
										setB2bSearch(val);
									} else if (reason === "clear") {
										setB2bSearch("");
									}
								}}
								onChange={(_, val) => setSelectedB2B(val)}
								renderInput={(params) => (
									<TextField
										{...params}
										label="B2B Member"
										placeholder="Search by company or name"
										size="small"
										required
										InputLabelProps={{ shrink: true }}
									/>
								)}
							/>
						</Grid>

						{/* Invoice Date */}
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								required
								size="small"
								label="Invoice Date"
								type="date"
								value={invoiceDate}
								onChange={(e) => setInvoiceDate(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>

						{/* Due Date */}
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								required
								size="small"
								label="Due Date"
								type="date"
								value={dueDate}
								onChange={(e) => setDueDate(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			{/* Card 2: Invoice Items */}
			<Card
				variant="outlined"
				sx={{
					borderRadius: 2,
					borderColor: "divider",
					boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
				}}
			>
				<CardContent sx={{ px: 2, py: 1.25, "&:last-child": { pb: 1.25 } }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 1,
						}}
					>
						<Typography variant="subtitle2" fontWeight={600} color="text.primary">
							Invoice Items
						</Typography>
						<Button
							type="button"
							size="small"
							variant="outlined"
							startIcon={<AddCircleOutlineIcon />}
							onClick={handleAddItem}
							sx={{ textTransform: "none", fontWeight: 600, py: 0.2 }}
						>
							Add Item
						</Button>
					</Box>

					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						{items.map((item, index) => {
							const lineTotal =
								(Number(item.quantity) || 0) * (Number(item.amount) || 0);

							return (
								<Box
									key={index}
									sx={{
										px: 1.5,
										py: 1.5,
										borderRadius: 1.5,
										bgcolor: (theme) =>
											theme.palette.mode === "dark"
												? "background.paper"
												: "#f8fafc",
										border: "1px solid",
										borderColor: "divider",
									}}
								>
									<Grid container spacing={1.5} alignItems="center">
										{/* Item Select */}
										<Grid item xs={12} sm={5} md={6}>
											<FormControl fullWidth size="small" required>
												<InputLabel id={`item-select-${index}`}>
													Select Item
												</InputLabel>
												<Select
													labelId={`item-select-${index}`}
													label="Select Item"
													value={item.id}
													onChange={(e) =>
														handleSelectPayableItem(
															index,
															e.target.value as number | ""
														)
													}
												>
													<MenuItem value="">Select an Item</MenuItem>
													{payableItems.map((p) => (
														<MenuItem key={p.id} value={p.id}>
															{p.name} ({Number(p.amount).toLocaleString()} BDT)
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>

										{/* Quantity */}
										<Grid item xs={5} sm={3} md={2}>
											<TextField
												fullWidth
												required
												size="small"
												type="number"
												label="Qty"
												inputProps={{ min: 1 }}
												value={item.quantity}
												onChange={(e) =>
													handleItemChange(
														index,
														"quantity",
														Math.max(1, Number(e.target.value))
													)
												}
											/>
										</Grid>

										{/* Amount */}
										<Grid item xs={5} sm={3} md={3}>
											<TextField
												fullWidth
												required
												size="small"
												type="number"
												label="Amount"
												inputProps={{ min: 0 }}
												value={item.amount}
												onChange={(e) =>
													handleItemChange(
														index,
														"amount",
														Math.max(0, Number(e.target.value))
													)
												}
											/>
										</Grid>

										{/* Action Delete */}
										<Grid
											item
											xs={2}
											sm={1}
											md={1}
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
											}}
										>
											<IconButton
												size="small"
												color="error"
												disabled={items.length <= 1}
												onClick={() => handleRemoveItem(index)}
											>
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										</Grid>
									</Grid>
								</Box>
							);
						})}
					</Box>
				</CardContent>
			</Card>

			{/* Card 3: Payment & Money Receipt (Optional) */}
			<Card
				variant="outlined"
				sx={{
					borderRadius: 2,
					borderColor: "divider",
					boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
				}}
			>
				<CardContent sx={{ px: 2, py: 1.25, "&:last-child": { pb: 1.25 } }}>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={withMoneyReceipt}
								onChange={(e) => setWithMoneyReceipt(e.target.checked)}
							/>
						}
						label={
							<Typography variant="subtitle2" fontWeight={600}>
								Generate Money Receipt with Invoice
							</Typography>
						}
					/>

					{withMoneyReceipt && (
						<Box sx={{ mt: 1, pt: 1, borderTop: "1px dashed", borderColor: "divider" }}>
							<Grid container spacing={2}>
								{/* Payment Method */}
								<Grid item xs={12} sm={6} md={4}>
									<FormControl fullWidth size="small" required>
										<InputLabel id="payment-method-label">
											Payment Method
										</InputLabel>
										<Select
											labelId="payment-method-label"
											label="Payment Method"
											value={paymentMethod}
											onChange={(e) => setPaymentMethod(e.target.value)}
										>
											<MenuItem value="Cash">Cash</MenuItem>
											<MenuItem value="Bank">Bank</MenuItem>
											<MenuItem value="Cheque">Cheque</MenuItem>
											<MenuItem value="Mobile Banking">Mobile Banking</MenuItem>
										</Select>
									</FormControl>
								</Grid>

								{/* Account */}
								<Grid item xs={12} sm={6} md={4}>
									<FormControl fullWidth size="small">
										<InputLabel id="account-label">Deposit Account</InputLabel>
										<Select
											labelId="account-label"
											label="Deposit Account"
											value={accountId}
											onChange={(e) =>
												setAccountId(e.target.value as number | "")
											}
										>
											<MenuItem value="">None / Not Selected</MenuItem>
											{accounts.map((ac) => (
												<MenuItem key={ac.ac_id} value={ac.ac_id}>
													{ac.ac_name} ({ac.ac_bank_name || ac.ac_type})
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{/* Cheque No */}
								{paymentMethod === "Cheque" && (
									<Grid item xs={12} sm={6} md={4}>
										<TextField
											fullWidth
											size="small"
											label="Cheque No."
											placeholder="Enter cheque number"
											value={chequeNo}
											onChange={(e) => setChequeNo(e.target.value)}
										/>
									</Grid>
								)}
							</Grid>
						</Box>
					)}
				</CardContent>
			</Card>

			{/* Card 4: Summary & Remarks */}
			<Card
				variant="outlined"
				sx={{
					borderRadius: 2,
					borderColor: "divider",
					boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
				}}
			>
				<CardContent sx={{ px: 2, py: 1.25, "&:last-child": { pb: 1.25 } }}>
					<Grid container spacing={2}>
						{/* Remarks */}
						<Grid item xs={12} md={7}>
							<TextField
								fullWidth
								multiline
								rows={3}
								size="small"
								label="Remark"
								placeholder="Add any internal or invoice notes..."
								value={remark}
								onChange={(e) => setRemark(e.target.value)}
							/>
						</Grid>

						{/* Calculations */}
						<Grid item xs={12} md={5}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									px: 1.5,
									py: 1.25,
									borderRadius: 1.5,
									bgcolor: (theme) =>
										theme.palette.mode === "dark"
											? "background.paper"
											: "#f8fafc",
									border: "1px solid",
									borderColor: "divider",
								}}
							>
								{/* Sub Total */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<Typography variant="body2" color="text.secondary">
										Sub Total:
									</Typography>
									<Typography variant="body2" fontWeight={600}>
										<span>{`${subTotal.toLocaleString()} BDT`}</span>
									</Typography>
								</Box>

								{/* Discount */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										gap: 1.5,
									}}
								>
									<Typography variant="body2" color="text.secondary">
										Discount:
									</Typography>
									<TextField
										size="small"
										type="number"
										inputProps={{ min: 0, style: { textAlign: "right", padding: "4px 8px" } }}
										sx={{ width: 110 }}
										value={discount}
										onChange={(e) =>
											setDiscount(
												e.target.value === ""
													? ""
													: Math.max(0, Number(e.target.value))
											)
										}
									/>
								</Box>

								{/* VAT */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										gap: 1.5,
									}}
								>
									<Typography variant="body2" color="text.secondary">
										VAT:
									</Typography>
									<TextField
										size="small"
										type="number"
										inputProps={{ min: 0, style: { textAlign: "right", padding: "4px 8px" } }}
										sx={{ width: 110 }}
										value={vat}
										onChange={(e) =>
											setVat(
												e.target.value === ""
													? ""
													: Math.max(0, Number(e.target.value))
											)
										}
									/>
								</Box>

								<Divider sx={{ my: 0.25 }} />

								{/* Net Total */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<Typography variant="subtitle2" fontWeight={700}>
										Net Total:
									</Typography>
									<Typography
										variant="h6"
										fontWeight={700}
										color="primary.main"
									>
										<span>{`${netTotal.toLocaleString()} BDT`}</span>
									</Typography>
								</Box>
							</Box>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			{/* Actions Footer */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					gap: 1.5,
					pt: 1,
				}}
			>
				{handleClose && (
					<Button
						variant="outlined"
						color="inherit"
						onClick={handleClose}
						disabled={isPending}
					>
						<span>Cancel</span>
					</Button>
				)}
				<Button
					type="submit"
					variant="contained"
					color="primary"
					disabled={isPending}
					sx={{ minWidth: 140, fontWeight: 600, textTransform: "none" }}
				>
					<span>{isPending ? "Creating..." : "Create Invoice"}</span>
				</Button>
			</Box>
		</Box>
	);
};

export default CreateB2BInvoiceForm;
